import { Component, ErrorInfo, ReactElement, ReactNode, useEffect } from 'react';
import { Alert, Modal } from 'antd';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import { BrowserAuthError, BrowserUtils } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';

import { loginRequest } from '../auth/authConfig';
import { appInsights } from '../telemetry';

import { isBeta } from '.';

type FallbackProps = {
    error: Error;
    componentStack: any;
    resetError: () => void;
};

export const showNewVersionModal = () => {
    Modal.confirm({
        title: 'New Release',
        icon: <ExclamationCircleOutlined />,
        content: (
            <>
                <p>A new version of the site has been released. Please click Reload to reload the page, or click Continue working to reload manually at a later time.</p>

                <Alert type="warning" description={<em>By continuing to work without a reload you may experience issues.</em>} />
            </>
        ),
        cancelText: 'Continue working...',
        okText: 'Reload',
        onCancel: () => {},
        onOk: async () => {
            const cacheKeys = await caches.keys();

            cacheKeys.forEach(async (key) => {
                await caches.delete(key);
            });

            if (!isBeta()) {
                window.location.reload();
            }
        },
    });
};

export const FallbackComponent = ({ error, componentStack, resetError }: FallbackProps): ReactElement => {
    const errorStr = error.toString();

    if (errorStr.includes('CanceledError')) {
        resetError();

        return <></>;
    }

    if (errorStr.includes('ChunkLoadError')) {
        resetError();
        showNewVersionModal();

        return <></>;
    }

    return <Alert type="error" title={error.toString()} />;
};

export const handleAppError = (error: Error) => {
    if (error.toString().includes('ChunkLoadError')) {
        showNewVersionModal();
    }
};

type AppErrorBoundaryProps = {
    children: ReactNode;
    onError?: (error: Error) => void;
};

type AppErrorBoundaryState = {
    error: Error | null;
    componentStack: string | null;
};

// Replaces Sentry.withErrorBoundary: reports the caught error to App Insights and
// still renders FallbackComponent so ChunkLoadError/CanceledError handling is preserved.
// (Sentry's showDialog user-feedback prompt has no App Insights equivalent and is dropped.)
export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
    state: AppErrorBoundaryState = { error: null, componentStack: null };

    static getDerivedStateFromError(error: Error): Partial<AppErrorBoundaryState> {
        return { error };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        this.setState({ componentStack: info.componentStack ?? null });

        appInsights.trackException({
            exception: error,
            properties: { componentStack: info.componentStack },
        });

        this.props.onError?.(error);
    }

    resetError = () => {
        this.setState({ error: null, componentStack: null });
    };

    render() {
        if (this.state.error) {
            return <FallbackComponent error={this.state.error} componentStack={this.state.componentStack} resetError={this.resetError} />;
        }

        return this.props.children;
    }
}

export function AuthErrorHandler({ error }) {
    const { instance } = useMsal();

    useEffect(() => {
        // Don't attempt redirect inside MSAL's silent iframe (would throw block_iframe_reload)
        if (error instanceof BrowserAuthError && !BrowserUtils.isInIframe()) {
            instance.loginRedirect(loginRequest);
        }
    }, [error, instance]);

    return null;
}
